import React from 'react'
import { useTranslation } from 'react-i18next'
import { 
  getBorderColor, 
  getBorderWidth, 
  getBackgroundColor, 
  getTextColorForSelection 
} from '../utils/orderPopupUtils'
import carIcon from '@/assets/images/car.svg'
import talbetIcon from '@/assets/images/talbet.svg'
import homeIcon from '@/assets/images/home1.svg'
import takeawayIcon from '@/assets/images/Frame1.svg'
import orderIcon from '@/assets/images/order.svg'

interface OrderType {
  id: number
  name: string
  icon?: string
  translationKey: string
}

interface TakeawayComponentsProps {
  selectedOrderType: string | null
  orderTypes: OrderType[]
  onOrderTypeSelected: (orderType: OrderType) => void
  isRTL: boolean
}

const ICON_OVERRIDES: Record<number, string> = {
  1: talbetIcon,
  2: takeawayIcon,
  3: homeIcon,
  4: carIcon,
}

const normalizeIconPath = (value?: string): string => {
  if (!value) {
    return orderIcon
  }

  if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('/')) {
    return value
  }

  let normalized = value.replace(/\\/g, '/')
  while (normalized.startsWith('../')) {
    normalized = normalized.slice(3)
  }
  while (normalized.startsWith('./')) {
    normalized = normalized.slice(2)
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`
  }

  return normalized
}

const getOrderTypeIcon = (orderType: OrderType): string => {
  if (ICON_OVERRIDES[orderType.id]) {
    return ICON_OVERRIDES[orderType.id]
  }

  if (orderType.icon) {
    return normalizeIconPath(orderType.icon)
  }

  return orderIcon
}

/**
 * Order type section component
 */
export const OrderTypeSection: React.FC<TakeawayComponentsProps> = ({
  selectedOrderType,
  orderTypes,
  onOrderTypeSelected,
  isRTL,
}) => {
  const { t } = useTranslation()

  return (
    <div className="w-full">
      <h3 className="text-base font-semibold text-gray-700 mb-3 font-['IBM_Plex_Sans_Arabic']">
        {t('order.order_type') || 'نوع الطلب'}
      </h3>

      <div className="flex gap-2">
        {orderTypes.map((orderType) => {
          const isSelected =
            selectedOrderType === orderType.translationKey ||
            (orderType.id === 1 && selectedOrderType?.includes('delivery')) ||
            (orderType.id === 2 && (selectedOrderType?.includes('takeaway') || selectedOrderType?.includes('take away'))) ||
            (orderType.id === 3 && selectedOrderType?.includes('dine-in')) ||
            (orderType.id === 4 && selectedOrderType?.includes('car pickup'))

          const iconSource = getOrderTypeIcon(orderType)

          return (
            <button
              key={orderType.id}
              type="button"
              onClick={() => onOrderTypeSelected(orderType)}
              aria-pressed={isSelected}
              className={`
                 h-[52px] px-5 flex items-center justify-center gap-2 rounded-xl   
                 flex-row-reverse 
              `}
              style={{
                backgroundColor: getBackgroundColor(isSelected),
                border: `${getBorderWidth(isSelected)}px solid ${getBorderColor(isSelected)}`,
                color: getTextColorForSelection(isSelected),
              }}
            >
              <span
                className="text-sm font-medium font-['IBM_Plex_Sans_Arabic']"
                style={{ color: getTextColorForSelection(isSelected) }}
              >
                {t(orderType.translationKey) || orderType.name}
              </span>
              <img
                src={iconSource}
                alt={orderType.name}
                className={`
                  w-5 h-5 flex-shrink-0 object-contain
                  ${isSelected ? 'brightness-0 invert' : 'opacity-80'}
                `}
              />
              
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Build order type section - wrapper for backward compatibility
 */
export const buildOrderTypeSection = (props: TakeawayComponentsProps): React.ReactElement => {
  return <OrderTypeSection {...props} />
}

/**
 * Continue button component
 */
export const ContinueButton: React.FC<{
  canContinue: boolean
  onPressed: () => void
  isSubmitting?: boolean
}> = ({
  canContinue,
  onPressed,
  isSubmitting = false,
}) => {
  const { t } = useTranslation()

  return (
    <button
      onClick={onPressed}
      disabled={!canContinue || isSubmitting}
      className={`
        w-full h-[60px] rounded-xl
        flex items-center justify-center gap-2
        font-semibold text-base font-['IBM_Plex_Sans_Arabic']
        transition-all
        ${canContinue && !isSubmitting
          ? 'hover:opacity-90'
          : 'cursor-not-allowed opacity-50'
        }
      `}
      style={{
        backgroundColor: canContinue && !isSubmitting ? 'var(--brand-primary)' : '#D1D5DB',
        color: canContinue && !isSubmitting ? '#FFFFFF' : '#9CA3AF',
      }}
    >
      {isSubmitting ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>{t('cs.processing') || 'جاري المعالجة...'}</span>
        </>
      ) : (
        <span>{t('cs.save_and_continue') || 'حفظ ومتابعة'}</span>
      )}
    </button>
  )
}

/**
 * Build continue button - wrapper for backward compatibility
 */
export const buildContinueButton = (props: {
  canContinue: boolean
  onPressed: () => void
  isSubmitting?: boolean
}): React.ReactElement => {
  return <ContinueButton {...props} />
}

